import { useEffect, useRef, useState } from "react";
import LockIcon from "@mui/icons-material/Lock";
import {
  Box,
  Chip,
  Paper,
  Typography,
  Button,
  Stack,
  ClickAwayListener,
  Portal,
} from "@mui/material";

export interface FacetOption {
  value: string;
  label: string;
}

interface FacetFilterProps {
  title: string;
  options: FacetOption[];
  selected: string[];
  onChange: (newValues: string[]) => void;
  maxInlineOptions?: number; // default 50
}

export default function FacetFilter({
  title,
  options,
  selected,
  onChange,
  maxInlineOptions = 70,
}: FacetFilterProps) {
  const [open, setOpen] = useState(false);
  const [draftSelected, setDraftSelected] = useState<string[]>(selected);
  const anchorRef = useRef<HTMLDivElement>(null);
  const closingRef = useRef(false);

  useEffect(() => {
    // Keep local draft in sync when closed so reopening reflects latest filters
    if (!open) {
      setDraftSelected(selected);
      closingRef.current = false;
    }
  }, [open, selected]);

  const isSameSelection = (a: string[], b: string[]) =>
    a.length === b.length && a.every((val, idx) => val === b[idx]);

  const openSheet = () => {
    if (options.length > maxInlineOptions) return; // Do NOT expand if too many
    setDraftSelected(selected);
    setOpen(true);
  };

  const handleSelect = (value: string) => {
    if (draftSelected.includes(value)) {
      setDraftSelected(draftSelected.filter((v) => v !== value));
    } else {
      setDraftSelected([...draftSelected, value]);
    }
  };

  const handleClear = () => {
    setDraftSelected([]);
  };

  const handleDone = () => {
    if (!open || closingRef.current) return;
    closingRef.current = true;
    setOpen(false);
    if (!isSameSelection(draftSelected, selected)) {
      onChange(draftSelected);
    }
  };

  const label = `${title} (${options.length})`;

  return (
    <Box ref={anchorRef} sx={{ display: "inline-block" }}>
      {/* Collapsed Chip */}
      <Chip
        label={label}
        variant={options.length > maxInlineOptions ? "filled" : "outlined"}
        color={selected.length > 0 && options.length <= maxInlineOptions ? "primary" : "default"}
        disabled={options.length > maxInlineOptions}
        icon={
          options.length > maxInlineOptions ? (
            <LockIcon fontSize="small" />
          ) : undefined
        }
        onClick={() => {
          if (open) {
            handleDone();
          } else {
            openSheet();
          }
        }}
        sx={{
          cursor: options.length > maxInlineOptions ? "not-allowed" : "pointer",
          bgcolor: options.length > maxInlineOptions ? "#e0e0e0" : "transparent",
          color: options.length > maxInlineOptions ? "#757575" : undefined,
        }}
      />

      {open && options.length <= maxInlineOptions && (
        <Portal>
          <ClickAwayListener onClickAway={handleDone}>
            <Paper
              elevation={4}
              sx={() => {
                const rect = anchorRef.current?.getBoundingClientRect();
                if (!rect) return { display: "none" };

                const popupWidth = 280;
                const popupHeight = 320;

                const left = Math.min(
                  rect.left,
                  window.innerWidth - popupWidth - 12
                );

                const spaceBelow = window.innerHeight - rect.bottom;
                const top =
                  spaceBelow > popupHeight
                    ? rect.bottom
                    : rect.top - popupHeight;

                return {
                  position: "fixed",
                  top,
                  left,
                  width: popupWidth,
                  maxHeight: popupHeight,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  zIndex: 2000,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "background.paper",
                };
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {title}
              </Typography>

              <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0, pr: 0.5 }}>
                <Stack spacing={1} direction="row" flexWrap="wrap" useFlexGap>
                  {options.map((opt) => (
                    <Chip
                      key={opt.value}
                      label={opt.label}
                      variant={draftSelected.includes(opt.value) ? "filled" : "outlined"}
                      color={draftSelected.includes(opt.value) ? "primary" : "default"}
                      onClick={() => handleSelect(opt.value)}
                      sx={{ m: 0.5 }}
                    />
                  ))}
                </Stack>
              </Box>

              <Stack
                direction="row"
                spacing={1}
                sx={{
                  mt: 2,
                  pt: 1,
                  borderTop: 1,
                  borderColor: "divider",
                  position: "sticky",
                  bottom: 0,
                  bgcolor: "background.paper",
                }}
              >
                <Button size="small" onClick={handleClear}>
                  Clear
                </Button>
                <Box flexGrow={1} />
                <Button size="small" variant="contained" onClick={handleDone}>
                  Done
                </Button>
              </Stack>
            </Paper>
          </ClickAwayListener>
        </Portal>
      )}
    </Box>
  );
}
